"use client";

import {
  Activity,
  AlertTriangle,
  ChevronDown,
  Cpu,
  MapPin,
  Maximize2,
  MoreVertical,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { GuardedActionLink } from "@/components/auth";
import { cn } from "@/lib/utils/cn";

import type { FleetKpiCounts } from "./fleet-kpi-strip";

/** Shared shell for fleet dashboard surfaces aligned with the CRM-style workspace system. */
export const fleetDashboardCardClasses =
  "rounded-control border border-card-border bg-surface-primary shadow-card";

const ACCENT = "text-primary";
const ACCENT_SOLID = "bg-primary text-primary-foreground shadow-card";

type RingSegment = {
  key: string;
  label: string;
  value: number;
  surfaceClass: string;
};

function buildRingSegments(counts: FleetKpiCounts): RingSegment[] {
  const raw: RingSegment[] = [
    { key: "unknown", label: "Unknown", value: counts.unknown, surfaceClass: "bg-surface-interactive text-foreground" },
    { key: "offline", label: "Offline", value: counts.offline, surfaceClass: "bg-status-offline text-foreground" },
    { key: "degraded", label: "Degraded", value: counts.degraded, surfaceClass: "bg-status-warning text-foreground" },
    { key: "healthy", label: "Healthy", value: counts.healthy, surfaceClass: "bg-status-success text-primary-foreground" },
  ];
  return raw.filter((s) => s.value > 0).sort((a, b) => a.value - b.value);
}

/** Left column: concentric health rings inspired by the migrated dashboard theme. */
export function FleetHealthRingsCard({ counts }: { counts: FleetKpiCounts }) {
  const segments = buildRingSegments(counts);
  const total = counts.total;

  return (
    <div className={cn(fleetDashboardCardClasses, "flex flex-col p-6 md:p-7")}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-sans text-lg font-semibold tracking-tight text-foreground">Fleet health</h2>
          <p className="mt-1 text-caption leading-relaxed text-muted">Live mix by connectivity status</p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-primary/10 px-3 py-1.5 font-sans text-micro font-semibold uppercase tracking-wider text-primary"
        >
          All devices
          <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
        </button>
      </div>

      <div className="relative mx-auto mt-2 flex min-h-[220px] w-full max-w-[280px] flex-1 items-center justify-center">
        {total === 0 ? (
          <p className="text-center text-caption text-muted">No loggers in this workspace yet.</p>
        ) : segments.length === 0 ? (
          <p className="text-center text-caption text-muted">No status breakdown available.</p>
        ) : (
          <div className="relative flex h-[200px] w-[220px] items-center justify-center">
            {segments.map((seg, idx) => {
              const diameter = 52 + idx * 42;
              const z = idx + 1;
              return (
                <div
                  key={seg.key}
                  className={cn(
                    "absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full font-sans text-caption font-bold shadow-card ring-2 ring-surface-primary",
                    seg.surfaceClass,
                  )}
                  style={{
                    width: diameter,
                    height: diameter,
                    zIndex: z,
                  }}
                  title={`${seg.label}: ${seg.value}`}
                >
                  <span className="drop-shadow-sm">{seg.value}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ul className="mt-auto space-y-2 border-t border-border/60 pt-4">
        {(
          [
            ["healthy", counts.healthy, "Healthy"],
            ["degraded", counts.degraded, "Degraded"],
            ["offline", counts.offline, "Offline"],
            ["unknown", counts.unknown, "Unknown"],
          ] as const
        ).map(([key, value, lab]) => (
          <li key={key} className="flex items-center justify-between text-caption">
            <span className="capitalize text-muted">{lab}</span>
            <span className={cn("font-semibold tabular-nums", ACCENT)}>{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type FleetActivityHubProps = {
  search: string;
  onSearchChange: (v: string) => void;
  healthFilter: string | null;
  onHealthFilter: (v: string | null) => void;
  counts: FleetKpiCounts;
};

const HEALTH_KEYS = [
  { key: "healthy", label: "Healthy" },
  { key: "degraded", label: "Degraded" },
  { key: "offline", label: "Offline" },
  { key: "unknown", label: "Unknown" },
] as const;

/** Right column: search, filter pills, and supporting fleet widgets. */
export function FleetActivityHub({
  search,
  onSearchChange,
  healthFilter,
  onHealthFilter,
  counts,
}: FleetActivityHubProps) {
  const total = counts.total || 0;
  const pctHealthy = total > 0 ? Math.round((counts.healthy / total) * 100) : 0;

  const barParts = [
    { key: "healthy", v: counts.healthy, className: "bg-primary" },
    { key: "degraded", v: counts.degraded, className: "bg-status-warning" },
    { key: "offline", v: counts.offline, className: "bg-status-offline" },
    { key: "unknown", v: counts.unknown, className: "bg-surface-interactive" },
  ];

  const warrantyAttention = counts.warranty30 + counts.warrantyExpired;

  return (
    <div className={cn(fleetDashboardCardClasses, "flex flex-col p-6 md:p-7")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-sans text-lg font-semibold tracking-tight text-foreground">Fleet activity</h2>
          <p className="mt-1 text-caption text-muted">Search, filter, and jump to hotspots</p>
        </div>
        <div className="flex shrink-0 items-center gap-ds-2">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted transition-colors hover:border-primary/20 hover:text-foreground"
            aria-label="More options"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted transition-colors hover:border-primary/20 hover:text-foreground"
            aria-label="Expand"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="relative mt-5">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
          aria-hidden
        />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search loggers by name or UID..."
          className="w-full rounded-control border border-card-border bg-surface-primary py-ds-3 pl-11 pr-ds-4 font-sans text-body text-foreground outline-none ring-0 transition-[border-color,box-shadow] placeholder:text-muted focus:border-primary focus:ring-2 focus:ring-ring"
          aria-label="Search fleet"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {HEALTH_KEYS.map(({ key, label }) => {
          const active = healthFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onHealthFilter(active ? null : key)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-sans text-caption font-medium transition-colors",
                active
                  ? cn("border-transparent", ACCENT_SOLID)
                  : "border-border bg-card text-muted hover:border-primary/20 hover:text-foreground",
              )}
            >
              <span
                className={cn("h-2 w-2 shrink-0 rounded-full", active ? "bg-card" : "bg-primary")}
                aria-hidden
              />
              {label}
              {active ? <X className="h-3.5 w-3.5 opacity-90" aria-hidden /> : null}
            </button>
          );
        })}
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        {/* Widget 1 - mix / bars */}
        <div className="rounded-control border border-card-border bg-surface-secondary p-ds-5">
          <p className={cn("font-sans text-3xl font-semibold tabular-nums tracking-tight", ACCENT)}>{pctHealthy}%</p>
          <p className="mt-1 text-caption text-muted">Fleet healthy</p>
          <div className="mt-5 flex h-[5.5rem] items-end gap-1.5">
            {barParts.map((p) => (
              <div
                key={p.key}
                className="flex min-h-[0.5rem] flex-1 flex-col justify-end"
                title={`${p.key}: ${p.v}`}
              >
                <div
                  className={cn("w-full min-h-[8px] rounded-t-md transition-all", p.className)}
                  style={{ height: total > 0 ? `${Math.max(10, (p.v / total) * 100)}%` : "8px" }}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={cn("h-1 rounded-full", i === 0 ? "w-5 bg-primary" : "w-1.5 bg-surface-interactive")}
              />
            ))}
          </div>
        </div>

        {/* Widget 2 - attention list */}
        <div className="rounded-control border border-card-border bg-surface-primary p-ds-5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-sans text-sm font-semibold text-foreground">Needs attention</p>
            <button
              type="button"
              className="rounded-full p-1 text-muted hover:bg-muted-bg/50"
              aria-label="Section menu"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </div>
          <ul className="mt-4 space-y-3">
            <li className="flex items-start gap-ds-3 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2.5">
              <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", ACCENT)} aria-hidden />
              <div className="min-w-0">
                <p className="font-sans text-caption font-medium text-foreground">Warranty windows</p>
                <p className="text-micro text-muted">
                  {warrantyAttention} logger{warrantyAttention === 1 ? "" : "s"} due within 30 days or expired
                </p>
              </div>
            </li>
            <li className="flex items-start gap-ds-3 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2.5">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" aria-hidden />
              <div className="min-w-0">
                <p className="font-sans text-caption font-medium text-foreground">Threshold alerts</p>
                <p className="text-micro text-muted">{counts.thresholdOpen24h} active in last 24h</p>
              </div>
            </li>
            <li className="flex items-start gap-ds-3 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2.5">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden />
              <div className="min-w-0">
                <p className="font-sans text-caption font-medium text-foreground">Map coverage</p>
                <p className="text-micro text-muted">
                  {counts.plottedFromMeta}/{counts.onMap} with precise coordinates
                </p>
              </div>
            </li>
          </ul>
        </div>

        {/* Widget 3 - workspace CTA */}
        <div
          className={cn(
            "flex flex-col rounded-control border border-card-border bg-surface-secondary p-ds-5",
          )}
        >
          <div
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-control bg-primary text-primary-foreground shadow-card",
            )}
          >
            <Activity className="h-5 w-5" aria-hidden />
          </div>
          <h3 className="mt-4 font-sans text-sm font-semibold text-foreground">Grow the fleet</h3>
          <p className="mt-1 text-caption leading-relaxed text-muted">
            Onboard a data logger to start streaming telemetry and alerts to this workspace.
          </p>
          <GuardedActionLink
            to="/devices/new"
            moduleKey="devices"
            action="create"
            deniedMessage="You don't have permission to create data loggers."
            className={cn(
              "btn btn-primary mt-auto w-full py-ds-3 text-body hover:opacity-95",
              ACCENT_SOLID,
            )}
          >
            <Cpu className="h-4 w-4" aria-hidden />
            Create data logger
          </GuardedActionLink>
        </div>
      </div>
    </div>
  );
}
